import { useCallback } from "react";
import { SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { OperationResult, useMutation } from "urql";

import { PageHeader } from "@/components/PageHeader";
import { graphql } from "@/gen";

import ArticleForm, { FormData } from "./components/ArticleForm";

const createRichTextArticleMutation = graphql(`
    mutation CreateRichTextArticle($input: CreateRichTextArticleInput!) {
        createRichTextArticle(input: $input) {
            id
        }
    }
`);

const createExternalUrlArticleMutation = graphql(`
    mutation CreateExternalUrlArticle($input: CreateExternalUrlArticleInput!) {
        createExternalUrlArticle(input: $input) {
            id
        }
    }
`);

const createStoryArticleMutation = graphql(`
    mutation CreateStoryArticle($input: CreateStoryArticleInput!) {
        createStoryArticle(input: $input) {
            id
        }
    }
`);

export default function NewArticleRoute() {
    const navigate = useNavigate();

    const [_createRichTextResult, createRichTextArticle] = useMutation(
        createRichTextArticleMutation
    );
    const [_createExternalUrlResult, createExternalUrlArticle] = useMutation(
        createExternalUrlArticleMutation
    );
    const [_createStoryArticleResult, createStoryArticle] = useMutation(createStoryArticleMutation);

    const handleSubmit: SubmitHandler<FormData> = useCallback(
        async data => {
            const handleResult = (result: OperationResult, id?: string) => {
                if (result.error) {
                    for (const error of result.error.graphQLErrors) {
                        toast.error("Error creating article", { description: error.message });
                    }
                } else {
                    toast.success("Article created");
                    navigate(`/articles/${id!}`);
                }
            };

            if (data.kind === "rich_text") {
                const { kind: _kind, ...input } = data;
                try {
                    const res = await createRichTextArticle({ input });
                    const id = res.data?.createRichTextArticle.id;
                    handleResult(res, id);
                } catch (e) {
                    toast.error("Request error", { description: String(e) });
                }
            }
            if (data.kind === "external_url") {
                const { kind: _kind, ...input } = data;
                try {
                    const res = await createExternalUrlArticle({ input });
                    handleResult(res, res.data?.createExternalUrlArticle.id);
                } catch (e) {
                    toast.error("Failed to create article", { description: String(e) });
                }
            }
            if (data.kind === "story") {
                const { kind: _kind, ...input } = data;
                try {
                    const res = await createStoryArticle({ input });
                    handleResult(res, res.data?.createStoryArticle.id);
                } catch (e) {
                    toast.error("Failed to create article", { description: String(e) });
                }
            }
        },
        [createExternalUrlArticle, createRichTextArticle, createStoryArticle, navigate]
    );

    return (
        <div className="m-4 flex flex-col gap-4">
            <PageHeader title="New article" />

            <ArticleForm onSubmit={handleSubmit} editableKind />
        </div>
    );
}
